import { useMemo } from 'react';
import { trpc } from '../lib/api';
import type {
  PaginatedWorkHistory,
  WorkStatistics,
} from '@shared/types/trpc/server/src/services/work-history-from-schedules.service';

export interface WorkHistoryListFilters {
  companyId?: string;
  equipmentId?: string;
  workType?: string;
  status?: 'completed' | 'incomplete' | 'cancelled';
  result?: 'good' | 'attention_needed' | 'repair_required';
  performedBy?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  search?: string;
  displayId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'startTime' | 'workTitle' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export function useWorkHistoryList(
  filters: WorkHistoryListFilters,
  options?: Parameters<typeof trpc.workHistory.list.useQuery>[1]
) {
  const input = useMemo(() => {
    return {
      companyId: filters.companyId ?? '',
      equipmentId: filters.equipmentId,
      workType: filters.workType,
      status: filters.status,
      result: filters.result,
      performedBy: filters.performedBy,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      search: filters.search,
      displayId: filters.displayId,
      page: filters.page ?? 1,
      limit: filters.limit ?? 20,
      sortBy: filters.sortBy ?? 'startTime',
      sortOrder: filters.sortOrder ?? 'desc'
    };
  }, [filters]);

  const query = trpc.workHistory.list.useQuery(input, options);
  return {
    ...query,
    data: query.data as PaginatedWorkHistory | undefined,
  };
}

export function useWorkHistoryStatistics(
  filters: Pick<WorkHistoryListFilters, 'companyId' | 'equipmentId' | 'startDate' | 'endDate'>,
  options?: Parameters<typeof trpc.workHistory.getStatistics.useQuery>[1]
) {
  const input = useMemo(() => {
    return {
      companyId: filters.companyId ?? '',
      equipmentId: filters.equipmentId,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined
    };
  }, [filters]);

  const query = trpc.workHistory.getStatistics.useQuery(input, options);
  return {
    ...query,
    data: query.data as WorkStatistics | undefined,
  };
}

export function useWorkHistoryDetail(
  params: {
    companyId?: string;
    displayId?: string;
    search?: string;
  },
  options?: Parameters<typeof trpc.workHistory.list.useQuery>[1]
) {
  const input = useMemo(() => {
    return {
      companyId: params.companyId ?? '',
      displayId: params.displayId,
      search: params.search,
      page: 1,
      limit: 1
    };
  }, [params.companyId, params.displayId, params.search]);

  const query = trpc.workHistory.list.useQuery(input, options);
  const data = query.data as PaginatedWorkHistory | undefined;

  return {
    ...query,
    data,
    record: data?.data?.[0] ?? null
  };
}
